// The value-first onboarding (A/B `symphonyValueFirstOnboarding`, variant B): the rules every surface draws it by.
//
// The harness draws the arm once and stamps it on the conversation; this client never conducts the release, it
// reads the stamp. `native-apps/shared/onboarding-flow/contract.json` is the one statement of what the stamp and
// the cards mean, and `test/value-first-onboarding-contract.test.ts` asserts every case of it against this file.
// iOS `ValueFirstOnboarding.swift` and Android `ValueFirstOnboarding.kt` are its twins.
//
// Import-free on purpose: the contract test must not drag the app's graph (see orion-web CLAUDE.md, "Tests").

export const ONBOARDING_FLOW_KEY = 'onboardingFlow';
export const ONBOARDING_STEP_KEY = 'onboardingStep';
export const VALUE_FIRST_FLOW = 'value-first';

export const ONBOARDING_STEPS = ['business', 'goal', 'task', 'team', 'connect', 'done'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** The stamp as the conversation (or the kickoff stream's `metadata` event) carries it. Raw strings. */
export interface OnboardingSeed {
  flow?: string;
  step?: string;
}

export interface OnboardingStamp {
  valueFirst: boolean;
  step: OnboardingStep | null;
}

export const TODAYS_ONBOARDING: OnboardingStamp = { valueFirst: false, step: null };

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const stepIndex = (step: string | undefined): number => ONBOARDING_STEPS.indexOf(step as OnboardingStep);

export function isOnboardingStep(value: unknown): value is OnboardingStep {
  return stepIndex(trimmed(value)) >= 0;
}

/** The flow and step off a metadata-shaped object: a conversation's metadata, or the kickoff `metadata` event. */
export function readOnboardingSeed(metadata: unknown): OnboardingSeed {
  const record = metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {};
  const flow = trimmed(record[ONBOARDING_FLOW_KEY]);
  const step = trimmed(record[ONBOARDING_STEP_KEY]);
  return { ...(flow ? { flow } : {}), ...(step ? { step } : {}) };
}

/** The stamp as the messages route sends it: the conversation metadata's own two keys, so one reader reads both. */
export type OnboardingWire = { onboardingFlow?: string; onboardingStep?: string };

export function onboardingWire(metadata: unknown): OnboardingWire {
  const seed = readOnboardingSeed(metadata);
  return { ...(seed.flow ? { onboardingFlow: seed.flow } : {}), ...(seed.step ? { onboardingStep: seed.step } : {}) };
}

/** A row's own `onboardingStep`, when it names a step this client knows. */
export function readRowOnboardingStep(metadata: unknown): OnboardingStep | undefined {
  const step = readOnboardingSeed(metadata).step;
  return isOnboardingStep(step) ? step : undefined;
}

/** The later of two steps. A step only moves forward (`stamp/never-back`); an unknown one never wins. */
export function laterStep(a: string | undefined, b: string | undefined): string | undefined {
  if (!isOnboardingStep(b)) return a ?? b;
  if (!isOnboardingStep(a)) return b;
  return stepIndex(b) > stepIndex(a) ? b : a;
}

/** A newer reading of the conversation's stamp folded onto the one held: the flow is kept, the step only moves forward. */
export function mergeOnboardingSeed(held: OnboardingSeed | null | undefined, next: OnboardingSeed): OnboardingSeed {
  const flow = next.flow ?? held?.flow;
  const step = laterStep(held?.step, next.step);
  return { ...(flow ? { flow } : {}), ...(step ? { step } : {}) };
}

/**
 * The flow and the current step: the conversation's stamp, moved on by the reply rows that carry one.
 * Not value-first ('control', or no stamp) is today's onboarding with no step at all. A value-first stamp whose
 * step write failed starts at the first step.
 */
export function resolveOnboardingStamp(
  seed: OnboardingSeed | null | undefined,
  rows: readonly { onboardingStep?: string }[] = [],
): OnboardingStamp {
  if (seed?.flow !== VALUE_FIRST_FLOW) return TODAYS_ONBOARDING;
  const step = rows.reduce<string | undefined>((held, row) => laterStep(held, row.onboardingStep), seed.step);
  return { valueFirst: true, step: isOnboardingStep(step) ? step : 'business' };
}

export type IslandTone = 'owner' | 'working';

export interface OnboardingChrome {
  /** The expanded island with its markers. False at `done`: the regular island returns. */
  expanded: boolean;
  /** How many of the markers are ticked. */
  ticked: number;
  labelKey: string;
  /** Green while it is the owner's turn, blue while agents work. */
  tone: IslandTone;
  /** The title above the chat. */
  titleKey: string;
  /** A confetti burst, once per conversation. */
  confetti: boolean;
}

/** Business, goal, team. Setup has no connect step any more; `connect` is kept for chats that reached it before. */
export const ISLAND_MARKERS = 3;

const CHROME: Record<OnboardingStep, OnboardingChrome> = {
  business: { expanded: true, ticked: 0, labelKey: 'valueFirst.island.business', tone: 'owner', titleKey: 'valueFirst.title.hello', confetti: false },
  goal: { expanded: true, ticked: 1, labelKey: 'valueFirst.island.goal', tone: 'owner', titleKey: 'valueFirst.title.team', confetti: false },
  task: { expanded: true, ticked: 1, labelKey: 'valueFirst.island.task', tone: 'working', titleKey: 'valueFirst.title.team', confetti: false },
  team: { expanded: true, ticked: 2, labelKey: 'valueFirst.island.team', tone: 'working', titleKey: 'valueFirst.title.team', confetti: false },
  connect: { expanded: true, ticked: 3, labelKey: 'valueFirst.island.connect', tone: 'working', titleKey: 'valueFirst.title.team', confetti: false },
  done: { expanded: false, ticked: 3, labelKey: 'valueFirst.island.done', tone: 'owner', titleKey: 'valueFirst.title.team', confetti: true },
};

/** The island and the title for a step. A step a newer server sends and this client does not know reads as the first one. */
export function onboardingChrome(step: string | null | undefined): OnboardingChrome {
  return isOnboardingStep(step) ? CHROME[step] : CHROME.business;
}

/** A goal card task's status line. Anything not working or done is up next. */
export function taskStatusKey(status: string | null | undefined): string {
  if (status === 'working') return 'valueFirst.task.drafting';
  if (status === 'done') return 'valueFirst.task.done';
  return 'valueFirst.task.upNext';
}

export type ProseTreatment = 'hidden' | 'docked' | 'bubble';

/** The onboarding's own cards. A row carrying one of these is drawn the value-first way; today's cards are not. */
export const VALUE_FIRST_CARD_KEYS = ['businessCard', 'bandOfferCard', 'teamCard', 'connectMeanwhileCard', 'goalCard'] as const;

/** What the prose rule needs to know about a Maestro row: the cards it carries (by key), and whether it asks with chips or a survey. */
export interface ProseRow {
  cards: readonly string[];
  choices?: boolean;
  survey?: boolean;
}

/**
 * How Maestro's words on a row are drawn (contract `prose`). During setup the chat holds cards, not conversation:
 * next to one of the onboarding's own cards the line is not drawn, and alone it is the docked question (only the
 * newest such row docks; `valueFirstLayout` decides which). A row with chips or a survey, and a row whose only cards
 * are today's, draw as today so their chips and cards stay usable. After `done`, and in today's onboarding, every
 * bubble is drawn as it is now.
 */
export function proseTreatment(input: { valueFirst: boolean; step: string | null | undefined; row: ProseRow }): ProseTreatment {
  if (!input.valueFirst || input.step === 'done') return 'bubble';
  if (input.row.choices || input.row.survey) return 'bubble';
  if (input.row.cards.some((key) => (VALUE_FIRST_CARD_KEYS as readonly string[]).includes(key))) return 'hidden';
  return input.row.cards.length > 0 ? 'bubble' : 'docked';
}

/** An owner row (contract `userRows`): a tapped answer is shown by the card it produced; typed words always show. */
export function userRowTreatment(input: { valueFirst: boolean; step: string | null | undefined; row: { choiceAnswer?: boolean; surveyAnswer?: boolean } }): 'hidden' | 'bubble' {
  if (!input.valueFirst || input.step === 'done') return 'bubble';
  return input.row.choiceAnswer || input.row.surveyAnswer ? 'hidden' : 'bubble';
}

export type DockKind = 'businessQuestion' | 'reading' | 'goalQuestion' | 'connect' | 'prose' | 'none';

/**
 * What the card above the composer holds (contract `dock`). At `business` the business question, or the "Reading
 * {site}…" spinner once a site was sent and before its business card arrives; the newest offer at `goal`; the newest
 * connect card at `connect`; else Maestro's newest lone question; nothing after setup.
 */
export function dockKind(step: string | null | undefined, state: { readingSite?: string; offer?: boolean; connect?: boolean; prose?: boolean }): DockKind {
  if (!isOnboardingStep(step) || step === 'done') return 'none';
  if (step === 'business') return state.readingSite ? 'reading' : 'businessQuestion';
  if (step === 'goal' && state.offer) return 'goalQuestion';
  if (step === 'connect' && state.connect) return 'connect';
  return state.prose ? 'prose' : 'none';
}

/**
 * The site being read: the owner's newest message is the site-read line (`valueFirst.site.readMessage`, already
 * resolved in the reader's language) and no business card has arrived after it.
 */
export function readingSiteFrom(rows: readonly { role?: string; content?: string; hasBusinessCard?: boolean }[], readMessage: string): string | undefined {
  const [prefix, suffix = ''] = readMessage.split('{url}');
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.hasBusinessCard) return undefined;
    if (row.role?.toUpperCase() !== 'USER') continue;
    const text = (row.content ?? '').trim();
    if (!prefix || !text.startsWith(prefix) || !text.endsWith(suffix)) return undefined;
    const site = text.slice(prefix.length, text.length - suffix.length).trim();
    return site || undefined;
  }
  return undefined;
}

// ---- The cards the flow adds (contract.json `cards`). Fail-closed readers, like lib/band-cards.ts. ----

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

const httpUrl = (value: unknown): string => (/^https?:\/\/\S+$/i.test(trimmed(value)) ? trimmed(value) : '');

export interface BusinessCardData {
  title: string;
  location?: string;
  /** Where the details were read from (a site's host), when they were. */
  source?: string;
  details?: { label: string; value: string }[];
  /** Read off the owner's Wix pack (screen 12). Only a real `true` on the wire. */
  fromWixPack?: true;
}

/** The business the owner described or the site that was read. The floor is a title. */
export function readBusinessCard(metadata: unknown): BusinessCardData | null {
  const card = asRecord(asRecord(metadata)?.businessCard);
  const title = trimmed(card?.title);
  if (!card || !title) return null;
  const details = (Array.isArray(card.details) ? card.details : []).flatMap((entry) => {
    const detail = asRecord(entry);
    const label = trimmed(detail?.label);
    const value = trimmed(detail?.value);
    return label && value ? [{ label, value }] : [];
  });
  const location = trimmed(card.location);
  const source = trimmed(card.source);
  return {
    title,
    ...(location ? { location } : {}),
    ...(source ? { source } : {}),
    ...(details.length > 0 ? { details } : {}),
    ...(card.fromWixPack === true ? { fromWixPack: true as const } : {}),
  };
}

export interface BusinessCardCopy {
  tagKey: string;
  /** A fixed title; null draws the card's own title and location. */
  titleKey: string | null;
  /** The line beside Edit; null when there is none. */
  lineKey: string | null;
}

/**
 * The words around a business card (contract `wixPack.businessCard`). A card read off the Wix pack has its own tag,
 * title and line, and carries the business itself in its details; any other card is titled by the business.
 */
export function businessCardCopy(card: BusinessCardData): BusinessCardCopy {
  if (card.fromWixPack) {
    return { tagKey: 'valueFirst.businessCard.tagWixPack', titleKey: 'valueFirst.businessCard.titleWixPack', lineKey: 'valueFirst.businessCard.lineWixPack' };
  }
  return { tagKey: card.source ? 'valueFirst.businessCard.tagFrom' : 'valueFirst.businessCard.tag', titleKey: null, lineKey: null };
}

/** The goal question's hint (contract `wixPack.goalHint`): "I know your site" once the chat's business card or any option came from the Wix pack. */
export function goalHintKey(businessFromWixPack: boolean, offer: { options: readonly { fromWixPack?: boolean }[] }): string {
  return businessFromWixPack || offer.options.some((option) => option.fromWixPack === true) ? 'valueFirst.goal.hintWixPack' : 'valueFirst.goal.hint';
}

/** Whether an option's evidence line draws with the W mark (contract `wixPack.evidence`). */
export function evidenceHasWixMark(option: { fromWixPack?: boolean; evidence?: string }): boolean {
  return option.fromWixPack === true && !!option.evidence;
}

/** The text Edit puts back in the composer: what the card says about the business, in one line. */
export function businessCardEditText(card: BusinessCardData): string {
  return [card.title, card.location].filter(Boolean).join(', ');
}

export interface TeamCardAgentData {
  name: string;
  title: string;
  id?: string;
}

export interface TeamCardData {
  name: string;
  agents: TeamCardAgentData[];
  /** Set up from the owner's Wix pack (screen 13). Only a real `true` on the wire. */
  fromWixPack?: true;
  /** The team's apps already connected, drawn as the Connectors row. Absent when there are none. */
  connectors?: { toolkit: string; name: string }[];
}

/** The team built for the goal. The floor is a name and one agent. */
export function readTeamCard(metadata: unknown): TeamCardData | null {
  const card = asRecord(asRecord(metadata)?.teamCard);
  const name = trimmed(card?.name);
  const agents = (Array.isArray(card?.agents) ? card.agents : []).flatMap((entry) => {
    const agent = asRecord(entry);
    const agentName = trimmed(agent?.name);
    const id = trimmed(agent?.id);
    return agentName ? [{ name: agentName, title: trimmed(agent?.title), ...(id ? { id } : {}) }] : [];
  });
  if (!card || !name || agents.length === 0) return null;
  const connectors = (Array.isArray(card.connectors) ? card.connectors : []).flatMap((entry) => {
    const connector = asRecord(entry);
    const toolkit = trimmed(connector?.toolkit);
    const connectorName = trimmed(connector?.name);
    return toolkit && connectorName ? [{ toolkit, name: connectorName }] : [];
  });
  return {
    name,
    agents,
    ...(card.fromWixPack === true ? { fromWixPack: true as const } : {}),
    ...(connectors.length > 0 ? { connectors } : {}),
  };
}

/** The team card's subtitle (contract `wixPack.teamCard`). */
export function teamCardSubtitleKey(card: TeamCardData): string {
  return card.fromWixPack ? 'valueFirst.teamCard.subtitleWixPack' : 'valueFirst.teamCard.subtitle';
}

export interface ConnectMeanwhileCardData {
  apps: { toolkit: string; name: string }[];
  /** One link for every app: each app's Connect and Connect all open it. */
  connectUrl: string;
}

/** The apps the team uses, offered while it works. The floor is one app and an http(s) link: a forged scheme is never opened. */
export function readConnectMeanwhileCard(metadata: unknown): ConnectMeanwhileCardData | null {
  const card = asRecord(asRecord(metadata)?.connectMeanwhileCard);
  const connectUrl = httpUrl(card?.connectUrl);
  const apps = (Array.isArray(card?.apps) ? card.apps : []).flatMap((entry) => {
    const app = asRecord(entry);
    const toolkit = trimmed(app?.toolkit);
    const name = trimmed(app?.name);
    return toolkit && name ? [{ toolkit, name }] : [];
  });
  if (!card || !connectUrl || apps.length === 0) return null;
  return { apps, connectUrl };
}

/** The business chips at `business`, in the order drawn after "Look at my site or store first". */
export const BUSINESS_KIND_KEYS = [
  'valueFirst.business.kind.yoga',
  'valueFirst.business.kind.salon',
  'valueFirst.business.kind.trainer',
  'valueFirst.business.kind.bakery',
  'valueFirst.business.kind.week',
] as const;

export type ValueFirstPickState = 'idle' | 'picked' | 'waiting';

/**
 * How one choice on a docked question draws (a business chip, a goal option). Once the owner taps one it stays marked
 * as their pick and the others wait, until the reply ends. iOS and Android `ValueFirstOnboarding.pickState` are its
 * twins.
 */
export function valueFirstPickState(id: string, picked: string | null, busy: boolean): ValueFirstPickState {
  if (picked) return id === picked ? 'picked' : 'waiting';
  return busy ? 'waiting' : 'idle';
}

/** The harness's connect walk takes at most this many apps (ConnectLinks.MAX_CHAIN_APPS). */
const MAX_WALK_STEPS = 12;

/**
 * Where one app's Connect on the connect card goes: the walk's own step for that app. The card's apps are the walk's
 * steps, in order (ManageBandTool.connectMeanwhileCard and ConnectLinks.armWalk read the same list), so the app at
 * `index` is `step/<index>`. The walk then carries on to the next app still waiting. Anything that is not a step-0
 * link, or an app past the walk's cap, opens the walk from the start. iOS and Android `connectStepUrl` are its twins.
 */
export function connectStepUrl(connectUrl: string, index: number): string {
  if (index <= 0 || index >= MAX_WALK_STEPS) return connectUrl;
  return connectUrl.replace(/\/step\/0(?=$|[?#])/, `/step/${index}`);
}

/** The composer's placeholder while setup runs, by step. After `done` the chat keeps its own. */
export function composerPlaceholderKey(step: OnboardingStep | null, sitePicking = false): string | undefined {
  if (step === 'business') return sitePicking ? 'valueFirst.site.describe' : 'valueFirst.business.placeholder';
  if (step === 'goal') return 'valueFirst.goal.placeholder';
  if (step === null || step === 'done') return undefined;
  return 'valueFirst.composer.askMaestro';
}

/**
 * Whether the burst at `done` still has to fire for this conversation, and records that it fired. Once per
 * conversation, across reloads where storage allows: a reopened finished chat must not celebrate again.
 */
export function claimConfetti(conversationId: string, storage: Pick<Storage, 'getItem' | 'setItem'> | null, fired: Set<string>): boolean {
  if (fired.has(conversationId)) return false;
  fired.add(conversationId);
  const key = `symphony.valueFirst.confetti.${conversationId}`;
  try {
    if (storage?.getItem(key)) return false;
    storage?.setItem(key, '1');
  } catch {
    // Storage blocked: the in-memory set still keeps it to once per page.
  }
  return true;
}

/** What the transcript layout needs to know about one row. */
export interface ValueFirstRow extends ProseRow {
  id?: string;
  role?: string;
  /** An owner row that answered with a tap. */
  choiceAnswer?: boolean;
  /** The question a row's chips ask, and the question an owner row answered. */
  askId?: string;
  answerAskId?: string;
  surveyAnswer?: boolean;
  /** An error row draws as today. */
  error?: boolean;
}

export interface ValueFirstLayout {
  /** Rows the transcript does not draw: Maestro's lone prose (the newest one docks), the offer row, tapped answers. */
  hiddenRows: Set<string>;
  /** Rows drawn with their cards and without their words. */
  proseHidden: Set<string>;
  dockedProseId?: string;
  dockedOfferId?: string;
  /** The row whose connect card is docked at `connect`; the transcript draws that row without it. */
  dockedConnectId?: string;
  /** Rows drawn without their chips: the owner already answered that question. */
  chipsHidden: Set<string>;
  /** Rows drawn without their business card: a newer card replaces it, as after an edit. */
  businessCardHidden: Set<string>;
}

const EMPTY_LAYOUT: ValueFirstLayout = { hiddenRows: new Set(), proseHidden: new Set(), chipsHidden: new Set(), businessCardHidden: new Set() };

/** The onboarding's own taps (contract `taps`): the card each one produced shows the answer. */
const isOnboardingAsk = (askId: string | undefined) => !!askId && askId.startsWith('valueFirst:');
const isAssistantRole = (role: string | undefined) => role === 'ASSISTANT' || role === 'ASSISTANT_STEP';

/**
 * Where each row goes while setup runs, per `prose`, `userRows` and `transcript`: a tapped owner answer is not drawn;
 * Maestro's words beside an onboarding card are not drawn and an offer-only row leaves the list; lone prose leaves the
 * list and only Maestro's newest row docks. Lone prose that answers words the owner typed (until a reply with one of the
 * onboarding's own cards resumes setup) is conversation, so it stays a bubble, and so does the newest lone prose while the dock holds the business question (once the owner has spoken) or the connect card. The offer
 * docks at `goal` until the owner types: their words answer it, so it leaves at once, and the harness sends it again
 * when it wants a pick. Only the newest business card is drawn, so an edit replaces the card. The newest connect card
 * docks at `connect`. Rows with chips, a survey, an error or only today's cards draw as today. After `done`, and
 * outside a value-first chat, nothing moves.
 */
export function valueFirstLayout(rows: readonly ValueFirstRow[], stamp: OnboardingStamp): ValueFirstLayout {
  if (!stamp.valueFirst || stamp.step === 'done') return EMPTY_LAYOUT;
  const hiddenRows = new Set<string>();
  const proseHidden = new Set<string>();
  // A question's chips leave once the owner answered it AFTER that row (a re-ask keeps its own); the answer then draws
  // as their bubble, unless it is one of the onboarding's own taps, which the card it produced already shows.
  const chipsHidden = new Set<string>();
  const answeredLater = new Set<string>();
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.role === 'USER' && row.answerAskId) answeredLater.add(row.answerAskId);
    else if (row.id && row.askId && answeredLater.has(row.askId)) chipsHidden.add(row.id);
  }
  const businessCardHidden = new Set<string>();
  let newerBusinessCard = false;
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (!row.id || !isAssistantRole(row.role) || !row.cards.includes('businessCard')) continue;
    if (newerBusinessCard) businessCardHidden.add(row.id);
    newerBusinessCard = true;
  }
  let newestAssistant: ValueFirstRow | undefined;
  let newestOffer: ValueFirstRow | undefined;
  let dockedConnectId: string | undefined;
  let typedSinceOffer = false;
  let ownerTyped = false;
  let ownerSpoke = false;
  let ownerBeforeNewest = false;
  for (const row of rows) {
    if (!row.id) continue;
    if (row.role === 'USER') {
      ownerSpoke = true;
      ownerTyped = !row.choiceAnswer && !row.surveyAnswer;
      if (ownerTyped) typedSinceOffer = true;
      const answerShown = !!row.answerAskId && !isOnboardingAsk(row.answerAskId);
      if (!answerShown && userRowTreatment({ valueFirst: true, step: stamp.step, row }) === 'hidden') hiddenRows.add(row.id);
      continue;
    }
    if (!isAssistantRole(row.role)) continue;
    newestAssistant = row;
    if (row.cards.includes('bandOfferCard') && !row.error) {
      newestOffer = row;
      typedSinceOffer = false;
    }
    ownerBeforeNewest = ownerSpoke;
    if (stamp.step === 'connect' && row.cards.includes('connectMeanwhileCard')) dockedConnectId = row.id;
    if (row.error) continue;
    const treatment = proseTreatment({ valueFirst: true, step: stamp.step, row });
    if (treatment === 'bubble' || (treatment === 'docked' && ownerTyped)) continue;
    // A reply with one of the onboarding's own cards resumes setup: the lines after it are setup questions again.
    if (treatment === 'hidden') ownerTyped = false;
    const cards = businessCardHidden.has(row.id) ? row.cards.filter((key) => key !== 'businessCard') : row.cards;
    const onlyOffer = cards.length > 0 && cards.every((key) => key === 'bandOfferCard');
    if (treatment === 'docked' || onlyOffer) hiddenRows.add(row.id);
    else proseHidden.add(row.id);
  }
  let dockedOfferId: string | undefined;
  let dockedProseId: string | undefined;
  if (newestAssistant?.id && !newestAssistant.error) {
    if (stamp.step === 'goal' && newestOffer?.id && !typedSinceOffer) {
      // The offer stays docked under a reply that carries none (a business edit Maestro answered in words), so the goals
      // never leave before the owner answers them; that reply then reads as a bubble.
      dockedOfferId = newestOffer.id;
      if (newestAssistant !== newestOffer && hiddenRows.has(newestAssistant.id)) hiddenRows.delete(newestAssistant.id);
    } else if (stamp.step === 'goal' && newestAssistant.cards.includes('bandOfferCard')) {
      // Typed after the offer: their words answered it.
    } else if (hiddenRows.has(newestAssistant.id) && proseTreatment({ valueFirst: true, step: stamp.step, row: newestAssistant }) === 'docked') {
      // The greeting before the owner's first word is what the business question already asks, so it stays hidden.
      if ((stamp.step === 'business' && ownerBeforeNewest) || dockedConnectId) hiddenRows.delete(newestAssistant.id);
      else if (stamp.step !== 'business') dockedProseId = newestAssistant.id;
    }
  }
  return { hiddenRows, proseHidden, dockedProseId, dockedOfferId, dockedConnectId, chipsHidden, businessCardHidden };
}

/** What an answered question still draws: only its link chips, which open a page rather than answer. */
export function answeredChoices<T extends { options: readonly { url?: string }[] }>(choices: T | undefined): T | undefined {
  const links = choices?.options.filter((option) => !!option.url) ?? [];
  return choices && links.length > 0 ? { ...choices, options: links } : undefined;
}

/** The chip-answer a value-first tap is sent with (contract `taps`), so `userRows` can hide it after a reload too. */
export type ValueFirstTapAnswer = { askId: string; optionId: string };

export type ValueFirstTap =
  | { kind: 'business'; chipKey: string }
  | { kind: 'site'; metaSiteId?: string }
  | { kind: 'goal'; optionId: string }
  | { kind: 'notNow' }
  | { kind: 'skip'; question: 'business' | 'goal' }
  | { kind: 'businessEdit' };

export function valueFirstTapAnswer(tap: ValueFirstTap): ValueFirstTapAnswer {
  switch (tap.kind) {
    case 'business':
      return { askId: 'valueFirst:business', optionId: tap.chipKey };
    case 'site':
      return { askId: 'valueFirst:site', optionId: tap.metaSiteId?.trim() || 'link' };
    case 'goal':
      return { askId: 'valueFirst:goal', optionId: tap.optionId };
    case 'notNow':
      return { askId: 'valueFirst:goal', optionId: 'notNow' };
    case 'skip':
      return { askId: `valueFirst:${tap.question}`, optionId: 'skip' };
    case 'businessEdit':
      return { askId: 'valueFirst:businessEdit', optionId: 'edit' };
  }
}

/** The answer cards that fold, in the order a row draws them (contract `folds`). */
const ANSWER_CARD_ORDER = ['businessCard', 'goalCard', 'teamCard'] as const;

/**
 * Which answer cards draw folded to one line (contract `folds`), as `<rowId>:<cardKey>`: every one but the newest, so each step's
 * card arrives open and folds when the next step's card comes. Nothing folds after `done`, nor outside a value-first chat.
 */
export function foldedCards(rows: readonly { id?: string; role?: string; cards: readonly string[] }[], stamp: OnboardingStamp): Set<string> {
  const folded = new Set<string>();
  if (!stamp.valueFirst || stamp.step === 'done') return folded;
  const answers = rows.flatMap((row) =>
    row.id && isAssistantRole(row.role) ? ANSWER_CARD_ORDER.filter((key) => row.cards.includes(key)).map((key) => `${row.id}:${key}`) : [],
  );
  answers.slice(0, -1).forEach((key) => folded.add(key));
  return folded;
}

export type LiveTaskState = 'done' | 'waiting' | 'drafting' | 'upNext';

/** A goal-card task row's state from the LIVE task (contract `liveTaskStatus`), checked in this order. */
export function liveTaskState(task: { status?: string; awaitingDecision?: boolean; live?: boolean }): LiveTaskState {
  if (task.status === 'COMPLETED') return 'done';
  if (task.awaitingDecision || task.status === 'ACTION_NEEDED') return 'waiting';
  if (task.live) return 'drafting';
  return 'upNext';
}

export const LIVE_TASK_LABEL_KEYS: Record<LiveTaskState, string> = {
  done: 'valueFirst.task.done',
  waiting: 'tasks.status.waitingOnYou',
  drafting: 'valueFirst.task.drafting',
  upNext: 'valueFirst.task.upNext',
};
